import { format } from 'date-fns';
import { DailyNotificationStats } from './notificationAnalyticsService';
import { NotificationHistoryItem } from './notificationHistoryService';

export const csvExportService = {
  /**
   * Convert array of objects to CSV format
   */
  convertToCSV(data: any[], headers: string[]): string {
    const csvRows = [];
    
    // Add headers
    csvRows.push(headers.join(','));
    
    // Add data rows
    data.forEach(row => {
      const values = headers.map(header => {
        const value = row[header];
        // Escape quotes and wrap in quotes if contains comma or quote
        const escaped = String(value ?? '').replace(/"/g, '""');
        return escaped.includes(',') || escaped.includes('"') || escaped.includes('\n') 
          ? `"${escaped}"` 
          : escaped;
      });
      csvRows.push(values.join(','));
    });
    
    return csvRows.join('\n');
  },

  /**
   * Download CSV file
   */
  downloadCSV(csvContent: string, filename: string): void {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    URL.revokeObjectURL(url);
  },

  /**
   * Export notification history to CSV
   */
  exportNotificationHistory(notifications: NotificationHistoryItem[]): void {
    const headers = [
      'Date',
      'Time',
      'User',
      'User Type',
      'Team',
      'Entity Type',
      'Status',
      'Delivery Method',
      'Error Message'
    ];

    const csvData = notifications.map(notification => ({
      'Date': format(new Date(notification.created_at), 'yyyy-MM-dd'),
      'Time': format(new Date(notification.created_at), 'HH:mm:ss'),
      'User': notification.user_name || 'Unknown',
      'User Type': notification.user_type,
      'Team': notification.team_name || '-',
      'Entity Type': notification.related_entity_type || '-',
      'Status': notification.status,
      'Delivery Method': notification.delivery_method,
      'Error Message': notification.error_message || '-'
    }));

    const csv = this.convertToCSV(csvData, headers);
    const filename = `notification-history-${format(new Date(), 'yyyy-MM-dd-HHmmss')}.csv`;
    
    this.downloadCSV(csv, filename);
  },

  /**
   * Export daily analytics to CSV
   */
  exportDailyAnalytics(
    dailyStats: DailyNotificationStats[],
    dateRange: { from: Date; to: Date },
    entityType?: string
  ): void {
    const headers = [
      'Date',
      'Total',
      'Sent',
      'Failed',
      'Pending',
      'Success Rate (%)'
    ];

    const csvData = dailyStats.map(stat => ({
      'Date': stat.date,
      'Total': stat.total.toString(),
      'Sent': stat.sent.toString(),
      'Failed': stat.failed.toString(),
      'Pending': stat.pending.toString(),
      'Success Rate (%)': stat.successRate.toFixed(2)
    }));

    const csv = this.convertToCSV(csvData, headers);
    
    // Add metadata header
    const metadata = [
      `Notification Analytics Export`,
      `Date Range: ${format(dateRange.from, 'yyyy-MM-dd')} to ${format(dateRange.to, 'yyyy-MM-dd')}`,
      `Entity Type: ${entityType || 'All'}`,
      `Generated: ${format(new Date(), 'yyyy-MM-dd HH:mm:ss')}`,
      '',
      ''
    ].join('\n');

    const fullCSV = metadata + csv;
    const filename = `notification-analytics-${format(new Date(), 'yyyy-MM-dd-HHmmss')}.csv`;
    
    this.downloadCSV(fullCSV, filename);
  },

  /**
   * Export summary statistics to CSV
   */
  exportSummaryStats(
    summary: {
      totalNotifications: number;
      totalSent: number;
      totalFailed: number;
      totalPending: number;
      overallSuccessRate: number;
      averagePerDay: number;
    },
    entityBreakdown: Array<{
      entityType: string;
      count: number;
      successRate: number;
    }>,
    dateRange: { from: Date; to: Date }
  ): void {
    const summaryHeaders = ['Metric', 'Value'];
    const summaryData = [
      { Metric: 'Total Notifications', Value: summary.totalNotifications },
      { Metric: 'Total Sent', Value: summary.totalSent },
      { Metric: 'Total Failed', Value: summary.totalFailed },
      { Metric: 'Total Pending', Value: summary.totalPending },
      { Metric: 'Overall Success Rate (%)', Value: summary.overallSuccessRate.toFixed(2) },
      { Metric: 'Average Per Day', Value: summary.averagePerDay.toFixed(2) }
    ];

    const breakdownHeaders = ['Entity Type', 'Count', 'Success Rate (%)'];
    const breakdownData = entityBreakdown.map(entity => ({
      'Entity Type': entity.entityType,
      'Count': entity.count,
      'Success Rate (%)': entity.successRate.toFixed(2)
    }));

    // Combine summary and breakdown
    const metadata = [
      `Notification Summary Statistics`,
      `Date Range: ${format(dateRange.from, 'yyyy-MM-dd')} to ${format(dateRange.to, 'yyyy-MM-dd')}`,
      `Generated: ${format(new Date(), 'yyyy-MM-dd HH:mm:ss')}`,
      '',
      'SUMMARY STATISTICS',
      ''
    ].join('\n');

    const summaryCsv = this.convertToCSV(summaryData, summaryHeaders);
    
    const breakdownSection = [
      '',
      '',
      'ENTITY BREAKDOWN',
      ''
    ].join('\n');
    
    const breakdownCsv = this.convertToCSV(breakdownData, breakdownHeaders);
    
    const fullCSV = metadata + summaryCsv + breakdownSection + breakdownCsv;
    const filename = `notification-summary-${format(new Date(), 'yyyy-MM-dd-HHmmss')}.csv`;
    
    this.downloadCSV(fullCSV, filename);
  },

  /**
   * Export complete analytics package (all data combined)
   */
  exportCompleteAnalytics(
    dailyStats: DailyNotificationStats[],
    summary: any,
    entityBreakdown: any[],
    dateRange: { from: Date; to: Date },
    entityType?: string
  ): void {
    const metadata = [
      `Complete Notification Analytics Export`,
      `Date Range: ${format(dateRange.from, 'yyyy-MM-dd')} to ${format(dateRange.to, 'yyyy-MM-dd')}`,
      `Entity Type: ${entityType || 'All'}`,
      `Generated: ${format(new Date(), 'yyyy-MM-dd HH:mm:ss')}`,
      '',
      'SUMMARY',
      `Total Notifications,${summary.totalNotifications}`,
      `Total Sent,${summary.totalSent}`,
      `Total Failed,${summary.totalFailed}`,
      `Total Pending,${summary.totalPending}`,
      `Success Rate (%),${summary.overallSuccessRate.toFixed(2)}`,
      `Average Per Day,${summary.averagePerDay.toFixed(2)}`,
      '',
      '',
      'DAILY STATISTICS'
    ].join('\n');

    const dailyHeaders = ['Date', 'Total', 'Sent', 'Failed', 'Pending', 'Success Rate (%)'];
    const dailyData = dailyStats.map(stat => ({
      'Date': stat.date,
      'Total': stat.total,
      'Sent': stat.sent,
      'Failed': stat.failed,
      'Pending': stat.pending,
      'Success Rate (%)': stat.successRate.toFixed(2)
    }));
    const dailyCsv = this.convertToCSV(dailyData, dailyHeaders);

    const breakdownSection = [
      '',
      '',
      'ENTITY BREAKDOWN'
    ].join('\n');

    const breakdownHeaders = ['Entity Type', 'Count', 'Success Rate (%)'];
    const breakdownData = entityBreakdown.map(entity => ({
      'Entity Type': entity.entityType,
      'Count': entity.count,
      'Success Rate (%)': entity.successRate.toFixed(2)
    }));
    const breakdownCsv = this.convertToCSV(breakdownData, breakdownHeaders);

    const fullCSV = metadata + '\n' + dailyCsv + breakdownSection + '\n' + breakdownCsv;
    const filename = `complete-analytics-${format(new Date(), 'yyyy-MM-dd-HHmmss')}.csv`;
    
    this.downloadCSV(fullCSV, filename);
  }
};
